/**
 * Painel de revisão + "Certificar e assinar" (E52.F3 · Design UX T3).
 *
 * O botão travado enquanto o checklist não fecha é **conforto**: a régua é do
 * servidor (`RevisaoIncompletaError` → 422, E52.F2). Por isso o painel não
 * "valida" nada além de habilitar o botão — e é por isso que o erro do
 * servidor tem lugar reservado na tela, mesmo com o botão travado.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Blueprint, Button, CardKicker, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { Modalidade } from '@/lib/cases';
import type { CertificacaoPendente, ChecklistRevisao } from '@/lib/certificacao';

const t = ptBR.certificacao;

const VAZIO: ChecklistRevisao = { metodologia: false, comparativos: false, texto: false };

/**
 * Os três pontos do checklist (ADR-0063). O DADO é o mesmo nas duas
 * modalidades — "conferi a metodologia / as evidências / o texto" — e por isso
 * os campos não mudam (ADR-0075 §8). O rótulo do segundo, sim: num caso
 * eletrônico não há comparativo de traço a validar, e pedir ao certificador que
 * confirme algo que não existe é pedir que ele marque no automático.
 */
function itensDoChecklist(
  modalidade: Modalidade | undefined,
): ReadonlyArray<{ campo: keyof ChecklistRevisao; label: string }> {
  return [
    { campo: 'metodologia', label: t.checklistMetodologia },
    {
      campo: 'comparativos',
      label: modalidade === 'eletronica' ? t.checklistEvidencias : t.checklistComparativos,
    },
    { campo: 'texto', label: t.checklistTexto },
  ];
}

export interface RevisaoPanelProps {
  item: CertificacaoPendente;
  assinando: boolean;
  erro: string | null;
  onAssinar: (checklist: ChecklistRevisao) => void;
  onFechar: () => void;
}

export function RevisaoPanel({ item, assinando, erro, onAssinar, onFechar }: RevisaoPanelProps) {
  const [checklist, setChecklist] = useState<ChecklistRevisao>(VAZIO);
  const completo = checklist.metodologia && checklist.comparativos && checklist.texto;
  const itens = itensDoChecklist(item.modalidade);

  return (
    <Blueprint
      as="section"
      data-testid="revisao-panel"
      aria-label={t.revisaoTitle}
      className="p-sc-4"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-sc-2">
        <div>
          <CardKicker className="block">{t.revisaoKicker}</CardKicker>
          <h2 className="mt-1 text-heading-sm text-fg">
            {t.revisaoTitle} <MonoData>{item.ccb_number ?? t.semValor}</MonoData>
          </h2>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="revisao-fechar"
          onClick={onFechar}
        >
          {t.revisaoFechar}
        </Button>
      </header>
      <p className="mt-1 text-body-sm text-fg-muted">{t.revisaoHint}</p>

      <Link
        to={`/casos/${item.case_id}/laudo`}
        data-testid="revisao-abrir-laudo"
        className="mt-sc-3 inline-block text-body-sm text-accent-700 underline-offset-4 hover:underline"
      >
        {t.abrirLaudo}
      </Link>

      <ul className="mt-sc-3 flex flex-col gap-sc-2">
        {itens.map(({ campo, label }) => (
          <li key={campo}>
            <label className="flex items-start gap-sc-2 text-body-sm text-fg">
              <input
                type="checkbox"
                data-testid={`checklist-${campo}`}
                checked={checklist[campo]}
                disabled={assinando}
                onChange={(e) => setChecklist((atual) => ({ ...atual, [campo]: e.target.checked }))}
                className="mt-0.5"
              />
              <span>{label}</span>
            </label>
          </li>
        ))}
      </ul>

      {!completo ? (
        <p data-testid="checklist-pendente" className="mt-sc-3 text-caption text-fg-muted">
          {t.checklistPendente}
        </p>
      ) : null}

      {erro !== null ? (
        <p
          role="alert"
          data-testid="revisao-erro"
          className="mt-sc-3 border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
        >
          {erro}
        </p>
      ) : null}

      {/* Assinar é o ato que a tela existe para produzir: único objeto sólido. */}
      <Button
        type="button"
        data-testid="assinar-laudo"
        disabled={!completo || assinando}
        onClick={() => onAssinar(checklist)}
        className="mt-sc-4"
      >
        {assinando ? t.assinando : t.assinar}
      </Button>
    </Blueprint>
  );
}
